import React, { useState, useEffect } from 'react';
import { debugLog, debugError } from '../../utils/debug';
import notificationManager from '../../utils/notifications';

const NotificationButton = () => {
  const [notificationStatus, setNotificationStatus] = useState('default');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkStatus = () => {
      const status = notificationManager.getStatus();
      
      if (!status.supported) {
        setNotificationStatus('unsupported');
        setIsVisible(false);
        return;
      }

      if (status.permission === 'granted') {
        setNotificationStatus('granted');
        setIsVisible(false);
      } else if (status.permission === 'denied') {
        setNotificationStatus('denied');
        setIsVisible(false);
      } else {
        setNotificationStatus('default');
        // Only show the button if we're in a standalone PWA on mobile
        const isStandalone = window.navigator.standalone || 
                            window.matchMedia('(display-mode: standalone)').matches;
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        setIsVisible(isStandalone && isMobile);
      }
    };

    checkStatus();
    
    // Check periodically in case permission changes
    const interval = setInterval(checkStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleRequestPermission = async () => {
    debugLog('🔔 User clicked notification permission button');
    
    try {
      const permission = await notificationManager.requestPermission();
      debugLog('🔔 Permission result:', permission);
      
      if (permission === 'granted') {
        setNotificationStatus('granted');
        setIsVisible(false);
        
        // Test notification
        setTimeout(() => {
          notificationManager.showNotification('🎉 Notifications Enabled!', {
            body: 'You\'ll now receive updates from your coach',
            tag: 'welcome-notification'
          });
        }, 500);
      } else {
        setNotificationStatus('denied');
        setIsVisible(false);
      }
    } catch (error) {
      debugError('Error requesting notification permission:', error);
      setNotificationStatus('denied');
      setIsVisible(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="mx-4 mb-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
            </svg>
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-100 mb-1">
            Get notified of new messages
          </h3>
          <p className="text-sm text-gray-300 mb-3">
            Receive push notifications when your coach sends you proactive messages or scheduled reminders.
          </p>
          
          <button
            onClick={handleRequestPermission}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
            </svg>
            Enable Notifications
          </button>
        </div>
        
        <button 
          onClick={() => setIsVisible(false)}
          className="flex-shrink-0 text-gray-400 hover:text-gray-200 transition-colors duration-200"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default NotificationButton; 